/** Total number of characters in a verification code (excludes hyphens). */
export const CODE_LENGTH = 12;

/** Number of characters per hyphen-separated group. */
export const CODE_GROUP_SIZE = 4;

/**
 * Normalise arbitrary input (typed, pasted, or scanned) into bare code
 * characters: uppercase, strip anything that is not A-Z or 0-9, and cap at
 * CODE_LENGTH. Hyphens and whitespace are removed.
 * @param raw: The raw string to normalise.
 * @returns Up to CODE_LENGTH uppercase alphanumeric characters.
 */
export const normaliseCodeInput = (raw: string): string =>
  raw
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, CODE_LENGTH);

/**
 * Format bare code characters for submission by inserting a hyphen between
 * each group of CODE_GROUP_SIZE, e.g. "1B5BC0682273" becomes
 * "1B5B-C068-2273". Partial input is grouped the same way.
 * @param chars: Bare (hyphen-free) code characters.
 * @returns The hyphen-grouped code string.
 */
export const formatCodeForSubmit = (chars: string): string => {
  const groups: Array<string> = [];
  for (let index = 0; index < chars.length; index += CODE_GROUP_SIZE) {
    groups.push(chars.slice(index, index + CODE_GROUP_SIZE));
  }
  return groups.join("-");
};

/**
 * Whether a bare code string is complete (exactly CODE_LENGTH characters).
 * @param chars: Bare (hyphen-free) code characters.
 * @returns True when the code has CODE_LENGTH characters.
 */
export const isCodeComplete = (chars: string): boolean =>
  chars.length === CODE_LENGTH;

/**
 * Extract the OTP from a scanned QR payload. If the payload is a URL carrying
 * a `code` query param (e.g. "/login?code=1B5B-C068-2273"), return that value;
 * otherwise return the trimmed payload. Normalisation happens separately.
 * @param raw: The raw decoded QR string.
 * @returns The code portion of the payload (still hyphenated/cased).
 */
export const extractScannedCode = (raw: string): string => {
  const trimmed = raw.trim();
  try {
    const parsed = new URL(trimmed, "https://placeholder.local");
    const codeParam = parsed.searchParams.get("code");
    if (codeParam) {
      return codeParam;
    }
  } catch {
    // Not URL-like; fall through to the raw value.
  }
  return trimmed;
};

/**
 * Overwrite characters in a code starting at a position, preserving any
 * characters after the written range. Used when typing or pasting into a
 * cell so editing a middle cell never discards later cells.
 * @param value: Current bare code characters.
 * @param index: Cell position to start writing at.
 * @param chars: Already-normalised characters to write.
 * @returns The updated, re-normalised bare code.
 */
export const overwriteCodeAt = (
  value: string,
  index: number,
  chars: string,
): string => {
  const written = normaliseCodeInput(chars);
  return normaliseCodeInput(
    value.slice(0, index) + written + value.slice(index + written.length),
  );
};

/**
 * Remove the character at a position, shifting later characters left.
 * @param value: Current bare code characters.
 * @param index: Cell position to remove.
 * @returns The updated, re-normalised bare code.
 */
export const removeCodeCharAt = (value: string, index: number): string =>
  normaliseCodeInput(value.slice(0, index) + value.slice(index + 1));
